// Vessel helpers for the refill wizard. A `Vessel` tags *where the pen
// physically is* when a reading is taken (orthogonal to a step's phase).
// Mirrors the Go service consts + TargetFor(phase) so the UI's defaults match
// the backend's when `target` is omitted.

import type { RefillStepPhase, Vessel } from "@/lib/api/types";

// Re-export so vessel-related code can import the type alongside the helpers
// from one place.
export type { Vessel } from "@/lib/api/types";

// The three vessels in mixing-order of the refill flow.
export const VESSELS: readonly Vessel[] = [
  "reservoir",
  "mixing_batch",
  "source_water",
] as const;

// Glanceable label (emoji + words) shown in the "Pen is in" control, the
// live hint, and step chips.
export const VESSEL_LABELS: Record<Vessel, string> = {
  reservoir: "🌱 Growing reservoir",
  mixing_batch: "🪣 Mixing batch",
  source_water: "💧 Source water",
};

// Short label for compact surfaces (toggle buttons, step chips).
export const VESSEL_SHORT_LABELS: Record<Vessel, string> = {
  reservoir: "🌱 Growing",
  mixing_batch: "🪣 Mixing",
  source_water: "💧 Source",
};

export function vesselLabel(v: Vessel): string {
  return VESSEL_LABELS[v];
}

export function vesselShortLabel(v: Vessel): string {
  return VESSEL_SHORT_LABELS[v];
}

// defaultVesselForStep maps a wizard step number to the vessel the pen is
// expected to be in. step 1 = reservoir baseline; 5/6 = the mixing batch
// (dose + pre-pour read); 7/8 = back in the reservoir (pour + final). All
// other (non-capture) steps fall back to reservoir.
export function defaultVesselForStep(step: number): Vessel {
  switch (step) {
    case 1:
      return "reservoir";
    case 5:
      return "mixing_batch";
    case 6:
      return "mixing_batch";
    case 7:
      return "reservoir";
    case 8:
      return "reservoir";
    default:
      return "reservoir";
  }
}

// defaultVesselForPhase mirrors the backend's TargetFor(phase): mix and
// pre_pour readings come from the mixing batch; everything else (pour,
// post_pour) is the reservoir.
export function defaultVesselForPhase(phase: RefillStepPhase): Vessel {
  switch (phase) {
    case "mix":
    case "pre_pour":
      return "mixing_batch";
    default:
      return "reservoir";
  }
}
